import fetcher, { adminFetcher } from '@lib/fetcher'
import metafieldsSetMutation from '@lib/mutations/metafields-set'
import {
	getCustomerFavoritesQuery,
	getCustomerFavoritesWithDetailsQuery
} from '@lib/queries/get-customer-favorites'
import type { CollectionProduct } from '@lib/types'
import type { RawFavorites } from '@lib/types/raw'
import { normalizeCollectionProducts } from '@lib/utils/normalizers'

/**
 *
 * @param customerAccessToken
 * @returns favorites object or null if no favorites
 */
export async function getCustomerFavorites(customerAccessToken: string | unknown) {
	if (!customerAccessToken) return null

	const customerFavoritesQuery = await fetcher({
		variables: { customerAccessToken },
		query: getCustomerFavoritesQuery
	})

	if (customerFavoritesQuery?.customer?.favorites?.value)
		return normalizeFavorites(customerFavoritesQuery?.customer?.favorites?.value)

	return null
}

/**
 * Returns an object with the metafield id and an array of favorites from the customer's metafield
 *
 * @param customerAccessToken
 * @returns { id: string, values: string[] }
 */
export async function getCustomerFavoritesAdmin(customerAccessToken: string | unknown) {
	if (!customerAccessToken) return null

	const customerFavoritesQuery = await fetcher({
		variables: { customerAccessToken },
		query: getCustomerFavoritesQuery
	})

	if (customerFavoritesQuery?.customer?.favorites?.value)
		return {
			id: customerFavoritesQuery?.customer?.favorites?.id,
			value: customerFavoritesQuery?.customer?.favorites.value
		}

	return null
}

function normalizeFavorites(favoritesRaw: string): string[] | null {
	const favoriteArr = JSON.parse(favoritesRaw).map(
		(favoriteProduct: RawFavorites) => `gid://shopify/Product/${favoriteProduct.p}`
	)

	// Not ordering by date b/c they should already be in the right order from adding to favorites
	// orderBy(favoriteArr, ['dateAdded'], ['desc'])

	if (favoriteArr.length) return favoriteArr as string[]
	return null
}

/**
 * Get the details of the customer's favorite products.
 *
 * @param customerAccessToken access token for customer
 * @returns the list of favorites with details
 */
export async function getCustomerFavoritesWithDetails(
	customerAccessToken: string | null
): Promise<CollectionProduct[] | null> {
	if (!customerAccessToken) return null

	const favoriteIds = await getCustomerFavorites(customerAccessToken)

	if (!favoriteIds) return null

	const customerFavoritesWithDetails = await fetcher({
		variables: { ids: favoriteIds },
		query: getCustomerFavoritesWithDetailsQuery
	})

	if (customerFavoritesWithDetails?.nodes)
		return normalizeCollectionProducts(
			customerFavoritesWithDetails?.nodes.map((node: unknown) => ({ node }))
		)

	return null
}

export async function updateCustomerFavorites(input: any) {
	const updateCustomerFavoriteProducts = await adminFetcher({
		variables: input,
		query: metafieldsSetMutation
	})

	return updateCustomerFavoriteProducts
}
